import { ReactNode } from "react";

import { Optional } from "common/utils/types";
import { useMatch, useNavigate } from "common/router/hooks";
import { CartCreatorKind } from "common/types/carts";

import { useClientQuery } from "client/accounts/hooks";
import {
  useActiveCartQuery,
  useCartRefillTooSoonSummaryQuery,
  useCartTaxQuery,
  useIsCartUpdating,
} from "client/carts/hooks";
import { useClinic } from "client/clinics/hooks";
import { Paths, urls } from "client/routes";
import { useCartDrawer } from "client/carts/stackables";

import { CheckoutStep, CheckoutStepProps } from "../types";
import CheckoutPage from "./CheckoutPage";
import { steps, inferStep, isValidStep, validators } from "../utils";
import ItemOptionsCheckoutStep from "./steps/ItemOptionsCheckoutStep";
import PaymentMethodCheckoutStep from "./steps/PaymentMethodCheckoutStep";
import DeliveryMethodCheckoutStep from "./steps/DeliveryMethodCheckoutStep";
import CheckoutAuthenticationProvider from "../providers/CheckoutAuthenticationProvider";
import CheckoutSummary from "./CheckoutSummary";
import CheckoutNavigation from "./CheckoutNavigation";
import EmptyCart from "./EmptyCart";

type CheckoutProps = {
  step: Optional<string>;
};

export type StepDefinition = {
  body: (props: CheckoutStepProps) => ReactNode;
};

const stepToDefinition: { [K in CheckoutStep]: StepDefinition } = {
  items: {
    body: (props) => <ItemOptionsCheckoutStep {...props} />,
  },
  delivery: { body: (props) => <DeliveryMethodCheckoutStep {...props} /> },
  payment: { body: (props) => <PaymentMethodCheckoutStep {...props} /> },
};

const CheckoutContent = ({ step }: CheckoutProps) => {
  const navigate = useNavigate();
  const { data: cart, isLoading: isCartLoading, error: cartError } = useActiveCartQuery();
  const cartId = cart?.id || 0;
  const isCartUpdating = useIsCartUpdating();
  const { open: openCartDrawer } = useCartDrawer();
  const { data: client, isLoading: isClientLoading, error: clientError } = useClientQuery();
  const clinic = useClinic();
  const { data: totalTax, error: taxError } = useCartTaxQuery(cartId, {
    enabled: step === "payment" && !!cart?.items.length && cartId !== 0,
  });
  const {
    data: refillTooSoonSummary,
    isLoading: isLoadingRefillTooSoonSummary,
    error: refillTooSoonSummaryError,
  } = useCartRefillTooSoonSummaryQuery(cartId, { enabled: cartId !== 0 });

  const taxAmount = totalTax?.total_tax;

  if (isCartLoading || isClientLoading || isLoadingRefillTooSoonSummary) {
    return <CheckoutPage.Loading />;
  }

  if (
    !cart ||
    !client ||
    cartError ||
    clientError ||
    !refillTooSoonSummary ||
    refillTooSoonSummaryError
  ) {
    const error = cartError || clientError;
    return (
      <CheckoutPage.RedirectInvalid
        dest={urls.checkout.error({ search: { status: error?.status } })}
      />
    );
  }

  if (!cart.items.length) {
    return (
      <CheckoutPage
        sider={<CheckoutSummary.Sider isLoading={isCartUpdating} cart={cart} tax={taxAmount} />}
      >
        <EmptyCart />
      </CheckoutPage>
    );
  }

  const inferredStep = inferStep(cart, refillTooSoonSummary);

  if (!inferredStep) {
    return <CheckoutPage.RedirectInvalid dest={urls.checkout.confirmation()} />;
  }

  if (!isValidStep(inferredStep, step)) {
    return (
      <CheckoutPage.RedirectInvalid
        dest={urls.checkout.detail({ params: { step: inferredStep } })}
      />
    );
  }

  const stepIndex = steps.indexOf(step);

  const navigateToStep = (nextStep: CheckoutStep) => {
    navigate(urls.checkout.detail({ params: { step: nextStep } }));
  };

  const onPrevious = () => {
    if (stepIndex) {
      navigateToStep(steps[stepIndex - 1]);
    } else {
      // don't open cart if it was clinic-created since we have different copy for the link
      if (cart.creator_type === CartCreatorKind.Client) {
        openCartDrawer();
      }
      navigate(urls.home.root());
    }
  };

  const onNext = () => {
    if (stepIndex < steps.length - 1) {
      navigateToStep(steps[stepIndex + 1]);
    } else {
      navigate(urls.checkout.confirmation());
    }
  };

  // If there are any cart messages, we have invalid items
  const isValid = validators[step](cart, refillTooSoonSummary) && !cart.warning_messages.length;

  return (
    <CheckoutPage
      onPrevious={onPrevious}
      navigation={
        <CheckoutNavigation cart={cart} steps={steps} step={step} onNavigate={navigateToStep} />
      }
      sider={<CheckoutSummary.Sider isLoading={isCartUpdating} cart={cart} tax={taxAmount} />}
    >
      {stepToDefinition[step].body({
        cart,
        client,
        clinic,
        onNext,
        onPrevious,
        isValid,
        isLoading: isCartUpdating,
        taxError,
        taxAmount,
        refillTooSoonSummary,
      })}
    </CheckoutPage>
  );
};

const Checkout = () => {
  const match = useMatch<Paths["checkout"]["detail"]>();
  return (
    <CheckoutAuthenticationProvider>
      <CheckoutContent step={match.params.step} />
    </CheckoutAuthenticationProvider>
  );
};

export default Checkout;
